// Copyright (c) Docugami, Inc. All rights reserved.

import { Dialog, DialogFooter, IDialogContentProps, IModalProps, ISpinnerStyles, PrimaryButton, Spinner } from "@fluentui/react";
import { NavigationType, useNavigationService } from "hooks/navigationService";
import { useAppDispatch } from 'hooks/redux';
import { WorkPathType } from "networking/WorkPathApi";
import { useEffect, useMemo } from "react";
import { setCurrentWorkPath, useCloneWorkPathsMutation } from "store/work_path/workPathSlice";
import styled from "styled-components";

const BodyContainer = styled.div`
    padding: 15px 0px;
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 15px;
`
interface CopyWorkPathDialogProps {
    workPathId: string;
    workPathName: string;
    workPathType: WorkPathType;
    clusterId: string;
    onDismiss: () => void;
}

export const CopyWorkPathDialog = (props: CopyWorkPathDialogProps): JSX.Element => {
    const { workPathId, workPathName, workPathType, clusterId, onDismiss } = props;
    const [triggerClone, { isLoading, isSuccess, isError, data }] = useCloneWorkPathsMutation();

    const dispatch = useAppDispatch();
    const navigation = useNavigationService();

    useEffect(() => {
        triggerClone({
            sourceWorkPathId: workPathId,
            workPath: {
                name: `Copy of ${workPathName}`,
                workPathType,
                clusterId
            }
        });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        []);

    useEffect(() => {
        if (!!data) {
            dispatch(setCurrentWorkPath(data));
        }
    }, [data, dispatch]);

    const footer = useMemo((): JSX.Element | null => {
        let content: JSX.Element | null;
        if (isSuccess && !!data) {
            content = <PrimaryButton onClick={() => navigation.navToWorkPathPage(NavigationType.PushNewPage, data!.id)}>Open Project</PrimaryButton>
        }
        else if (isError) {
            content = <PrimaryButton onClick={() => onDismiss()}>OK</PrimaryButton>
        }
        else {
            content = null;
        }

        return !!content ?
            <DialogFooter styles={{ actions: { display: "flex", justifyContent: "center" } }}>
                {content}
            </DialogFooter> :
            null;
    }, [isSuccess, isError, data, navigation, onDismiss]);

    const body = useMemo((): JSX.Element | null => {
        if (isLoading) {
            const spinnerStyle: Partial<ISpinnerStyles> = {
                circle: {
                    width: "50px",
                    height: "50px",
                    borderWidth: "4px"
                }
            }

            return (
                <BodyContainer>
                    <Spinner styles={spinnerStyle} />
                </BodyContainer>
            );
        }

        return null;
    }, [isLoading])

    const modalProps: IModalProps = {
        isBlocking: isLoading
    }

    const contentProps = useMemo((): IDialogContentProps => {
        if (isError) {
            return {
                title: "Uh oh!",
                subText: "Something went wrong while copying your project. Please try again later.",
                showCloseButton: true,
            }
        }
        else if (isSuccess) {
            return {
                title: "Copy Project",
                subText: "Your project was successfully copied!",
                showCloseButton: true
            }
        }
        else {
            return {
                title: "Copying project...",
                showCloseButton: false
            }
        }
    }, [isError, isSuccess]);

    return (
        <Dialog hidden={false} modalProps={modalProps} dialogContentProps={contentProps} onDismiss={onDismiss}>
            {body}
            {footer}
        </Dialog>
    )
}
